import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Row, Col, Button, List, Checkbox, Spin, Tabs, Typography, Descriptions, Popover, Badge } from 'antd';
import { CloseOutlined, FilterOutlined, UnorderedListOutlined, ExportOutlined } from '@ant-design/icons';
import Filter from './Filter';

const { Link, Title } = Typography;

const TrainingBrowseCSPModalForm = ({ open, onCancel, onLoad, dbTrainings, rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills }) => {
  const nonArchived = useMemo(() => (Array.isArray(dbTrainings) ? dbTrainings.filter((c) => !c.archived) : []), [dbTrainings]);

  const [filteredList, setFilteredList] = useState(nonArchived);
  const [checkedList, setCheckedList] = useState([]);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [resetFlag, setResetFlag] = useState(false);

  useEffect(() => {
    setFilteredList(nonArchived);
    setCheckedList([]);
    setSelectedTraining(null);
  }, [nonArchived, open]);

  const resetFilter = () => {
    setFilteredList(nonArchived);
    setResetFlag((prev) => !prev);
  };

  const handleFilterChange  = useCallback((filtered) => {
    const list = Array.isArray(filtered) ? filtered.filter((c) => !c.archived) : [];
    setFilteredList(list);
  }, [setFilteredList]);

  const onCheckboxChange = (id, training) => {
    setCheckedList((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setSelectedTraining(training);
  };

  const tabsItems = selectedTraining
    ? [
        {
          key: 'overview',
          label: 'Overview',
          children: (
            <>
              <h5>Description</h5>
              <p>{selectedTraining.description}</p>
              <Button icon={<ExportOutlined />} href={selectedTraining.link} target="_blank">
                Website
              </Button>
              <h5 className="rewire-title">Rewire skill groups</h5>
              {selectedTraining.skills_group
                .sort((a, b) => rsSkillsGroup.find((g) => g.id === a).name.localeCompare(rsSkillsGroup.find((g) => g.id === b).name))
                .map((groupId) => {
                  const grp = rsSkillsGroup.find((g) => g.id === groupId);
                  return (
                    <Popover
                      getPopupContainer={() => document.body}
                      overlayStyle={{
                        position: 'fixed',
                        overflow: 'visible',
                      }}
                      key={grp.id}
                      content={
                        <div className="skills-popover">
                          <Title level={4}>{grp.name}</Title>
                          <Title level={5}>Included Skills</Title>
                          <ul>{grp.specific_skills.length > 0 ? grp.specific_skills.map((sg, i) => <li key={i}>{rsSpecificSkills.find((e) => e.id === sg)?.name}</li>) : <span className="empty">None</span>}</ul>
                          <Title level={5}>Included Knowledge</Title>
                          <ul>{grp.specific_knowledge.length > 0 ? grp.specific_knowledge.map((sg, i) => <li key={i}>{rsSpecificKnowledge.find((e) => e.id === sg)?.name}</li>) : <span className="empty">None</span>}</ul>
                        </div>
                      }
                    >

                      <Badge count={grp.name} color={grp.color || 'white'} key={grp.id} className="skillBadge" />
                    </Popover>
                  );
                })}
            </>
          ),
        },
        {
          key: 'details',
          label: 'Details',
          children: (
            <div className="details-content-scroll">
              <Descriptions bordered column={1} layout="horizontal" size="small">
                <Descriptions.Item label="Organizer">{selectedTraining.organizer}</Descriptions.Item>
                <Descriptions.Item label="Country">{selectedTraining.country}</Descriptions.Item>
                <Descriptions.Item label="Language">{Array.isArray(selectedTraining.language) ? selectedTraining.language.join(', ') : selectedTraining.language}</Descriptions.Item>
                <Descriptions.Item label="Dates">{Array.isArray(selectedTraining.dates) ? selectedTraining.dates.join(', ') : selectedTraining.dates}</Descriptions.Item>
                <Descriptions.Item label="Duration">{selectedTraining.duration}</Descriptions.Item>
                <Descriptions.Item label="Can lead to certification">{selectedTraining.can_lead_to_certification}</Descriptions.Item>
                <Descriptions.Item label="Includes exams for certification">{selectedTraining.includes_exams_for_certification}</Descriptions.Item>
                <Descriptions.Item label="Content type">{selectedTraining.type_format}</Descriptions.Item>
                <Descriptions.Item label="Timing">{selectedTraining.timing}</Descriptions.Item>
                <Descriptions.Item label="Type/Format">{selectedTraining.type_format}</Descriptions.Item>
                <Descriptions.Item label="Price (€)">{selectedTraining.price}</Descriptions.Item>
              </Descriptions>
            </div>
          ),
        },
      ]
    : [];

  return (
    <Modal open={open} onCancel={onCancel} maskClosable={false} width="80vw" footer={null} title="Browse available trainings" className="browse-modal">
      <div className="browse-content">
        <div className="browse-body">
          <Row gutter={24}>
            <Col span={6} className="browse-col">
              <div className="col-title">
                <FilterOutlined /> Filter by
                <Button className="rst-button" type="secondary" icon={<CloseOutlined />} onClick={resetFilter}>
                  Reset
                </Button>
              </div>
              <div className="col-scroll">
                <Filter data={nonArchived} skillsGroup={rsSkillsGroup} toShow={handleFilterChange} resetFlag={resetFlag} />
              </div>
            </Col>

            <Col span={6} className="browse-col">
              <div className="col-title">
                <UnorderedListOutlined /> Trainings {filteredList.length}/{nonArchived.length}
              </div>
              <div className="col-scroll">
                <List
                  bordered
                  dataSource={filteredList}
                  renderItem={(item) => (
                    <List.Item>
                      <Checkbox checked={checkedList.includes(item.id)} onChange={() => onCheckboxChange(item.id, item)}>
                        {item.name}
                      </Checkbox>
                    </List.Item>
                  )}
                  locale={{ emptyText: <Spin /> }}
                />
              </div>
            </Col>

            <Col span={12} className="browse-col">
              {selectedTraining && <h2 className="col-title">{selectedTraining.name}</h2>}
              {selectedTraining ? <Tabs defaultActiveKey="overview" items={tabsItems} className="tabs-content-scroll" /> : <div className="col-details-empty">Select a training to see details.</div>}
            </Col>
          </Row>
        </div>
        <div className="browse-footer">
          <div className="footer-details">
            {checkedList.length === 1 ? '1 training selected' : checkedList.length > 1 ? `${checkedList.length} trainings selected` : 'No trainings selected'}
            {checkedList.length > 0 && (
              <Link onClick={() => setCheckedList([])} className="clear-link">
                <CloseOutlined /> Clear
              </Link>
            )}
          </div>
          <Button onClick={() => onLoad(checkedList)} type="primary" disabled={!checkedList.length}>
            Load Selected
          </Button>
          <Button onClick={onCancel}>Close</Button>
        </div>
      </div>
    </Modal>
  );
};

export default TrainingBrowseCSPModalForm;
